import type { RpcClient } from '../rpc'
import type { RundotGameMessageId } from '../RundotGameMessageId'

// Generic admin types — parameterized by entry and report shapes

export interface AdminGenBrowseParams {
  profileId?: string
  status?: 'active' | 'removed'
  cursor?: string
  limit?: number
  sortOrder?: 'asc' | 'desc'
}

export interface AdminGenBrowseResponse<TEntry> {
  entries: TEntry[]
  nextCursor?: string
}

export interface AdminGenListReportsParams {
  status?: 'pending' | 'reviewed' | 'dismissed'
  cursor?: string
  limit?: number
}

export interface AdminGenListReportsResponse<TReport> {
  reports: TReport[]
  nextCursor?: string
}

export type AdminGenResolveAction = 'reviewed' | 'dismissed'

export interface AdminGenApi<TEntry, TReport> {
  browse(params?: AdminGenBrowseParams): Promise<AdminGenBrowseResponse<TEntry>>
  removeEntry(generationId: string): Promise<void>
  listReports(params?: AdminGenListReportsParams): Promise<AdminGenListReportsResponse<TReport>>
  resolveReport(reportId: string, action: AdminGenResolveAction): Promise<void>
}

// --- RPC factory ---

export interface AdminGenMessageIds {
  browse: RundotGameMessageId
  removeEntry: RundotGameMessageId
  listReports: RundotGameMessageId
  resolveReport: RundotGameMessageId
}

export function createRpcAdminGenApi<TEntry, TReport>(
  rpcClient: RpcClient,
  messageIds: AdminGenMessageIds,
): AdminGenApi<TEntry, TReport> {
  return {
    async browse(params?: AdminGenBrowseParams): Promise<AdminGenBrowseResponse<TEntry>> {
      return rpcClient.call<AdminGenBrowseResponse<TEntry>>(messageIds.browse, params ?? {})
    },

    async removeEntry(generationId: string): Promise<void> {
      await rpcClient.call<void>(messageIds.removeEntry, { generationId })
    },

    async listReports(params?: AdminGenListReportsParams): Promise<AdminGenListReportsResponse<TReport>> {
      return rpcClient.call<AdminGenListReportsResponse<TReport>>(messageIds.listReports, params ?? {})
    },

    async resolveReport(reportId: string, action: AdminGenResolveAction): Promise<void> {
      await rpcClient.call<void>(messageIds.resolveReport, { reportId, action })
    },
  }
}

// --- Mock factory ---

export function createMockAdminGenApi<TEntry, TReport>(
  label: string,
): AdminGenApi<TEntry, TReport> {
  return {
    async browse(): Promise<AdminGenBrowseResponse<TEntry>> {
      console.warn(`[RUN:mock] ${label}.browse() returning empty results`)
      return { entries: [] }
    },

    async removeEntry(): Promise<void> {
      console.warn(`[RUN:mock] ${label}.removeEntry() is a no-op in mock mode`)
    },

    async listReports(): Promise<AdminGenListReportsResponse<TReport>> {
      console.warn(`[RUN:mock] ${label}.listReports() returning empty results`)
      return { reports: [] }
    },

    async resolveReport(): Promise<void> {
      console.warn(`[RUN:mock] ${label}.resolveReport() is a no-op in mock mode`)
    },
  }
}
